import React, { useCallback, useEffect, useContext, useState } from "react";
import { useFormik } from "formik";
import { Button, Col, Form, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import ButtonSubmitFormikPermission from "../../../components/UI/Button/ButtonSubmitFormikPermission";
import FormAlert from "../../../components/UI/FormAlert/FormAlert";
import OverlayInfoAdicional from "../../../components/UI/Overlays/OverlayInfoAdicional";
import { AuthContext } from "../../../context/AuthContext";
import funcionalidades from "../../../context/AuthContext.funcionalidades";
import useApiClient, { apiRoutes } from "../../../hooks/apiClient";
import TextInputFormik from "../../../components/UI/TextInput/TextInputFormik";
import CheckBoxFormik from "../../../components/UI/Checkbox/CheckBoxFormik";
import TextSpinner from "../../../components/UI/Spinner/TextSpinner";
import FormRoteiroResumo from "./FormRoteiroResumo";
import useAssociacaoAtividades from "./hooks/useAssociacaoAtividades";
import FormRoteiroOrdenacao from "./FormRoteiroOrdenacao";

const validationSchema = Yup.object().shape({
  sequencialRoteiro: Yup.string().default(""),
  sigla: Yup.string().default("").required("Informe a Sigla"),
  nome: Yup.string().default("").required("Informe o Nome"),
  descricao: Yup.string()
    .default("")
    .required("Informe a Descrição")
    .max(240, "A Descrição não pode ter mais de 240 caracteres"),
  ativo: Yup.boolean(),
  sequencialAtividadeOrdenacao: Yup.string().default(""),
});

const initialValues = {};

validationSchema._nodes.forEach((key) => {
  initialValues[key] = key === "ativo" ? true : "";
});

const FormRoteiro = ({ history, location }) => {
  const { state } = location;
  const { isAllowed } = useContext(AuthContext);
  const [viewBag, setViewBag] = useState({
    exibirAssociacao: false,
    exibirOrdenacao: false,
    exibirResumo: false,
  });
  const [showErro, setShowErro] = useState(false);
  const [messageErro, setMessageErro] = useState("");
  const [dadosInfo, setDadosInfo] = useState({});

  // Formik
  const { request: requestForm, data: dataForm, errors: errorForm, isLoading } = useApiClient();

  const handleSubmit = useCallback(
    (values) => {
      if (values.sequencialRoteiro) requestForm(apiRoutes.fiscalizacao.parametrizacao.alteraRoteiro, values);
      else requestForm(apiRoutes.fiscalizacao.parametrizacao.incluiRoteiro, values);
    },
    [requestForm],
  );

  const handleAssociacao = useCallback(() => {
    setViewBag((curr) => ({
      ...curr,
      exibirAssociacao: !curr.exibirAssociacao,
      exibirOrdenacao: false,
      exibirResumo: false,
    }));
  }, [setViewBag]);

  const handleOrdenacao = useCallback(() => {
    setViewBag((curr) => ({
      ...curr,
      exibirAssociacao: false,
      exibirOrdenacao: !curr.exibirOrdenacao,
      exibirResumo: false,
    }));
  }, [setViewBag]);

  const handleResumo = useCallback(() => {
    setViewBag((curr) => ({
      ...curr,
      exibirAssociacao: false,
      exibirOrdenacao: false,
      exibirResumo: !curr.exibirResumo,
    }));
  }, [setViewBag]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const { setSubmitting, setErrors, isSubmitting, isValid } = formik;

  useEffect(() => {
    if ((dataForm || errorForm) && isSubmitting && !isLoading) formik.setSubmitting(false);

    if (errorForm && isValid && !isLoading) formik.setErrors({ ...formik.errors, ...errorForm });
  }, [formik, setSubmitting, setErrors, isSubmitting, isValid, dataForm, errorForm, isLoading]);

  useEffect(() => {
    if (!dataForm && !errorForm) return;

    if (dataForm) {
      toast.success("Roteiro salvo com sucesso");
      history.push("/app/roteiro");
    }
  }, [dataForm, errorForm, history]);

  // Carregar item
  const {
    request: roteiroRequest,
    data: roteiroData,
    errors: roteiroErrors,
    isLoading: roteiroIsLoading,
  } = useApiClient();

  useEffect(() => {
    if (state?.itemKey) {
      roteiroRequest(apiRoutes.fiscalizacao.parametrizacao.consultaRoteiro, { id: state.itemKey });
      delete state.itemKey;
    }
  }, [roteiroRequest, state]);

  useEffect(() => {
    if ((!roteiroData && !roteiroErrors) || roteiroIsLoading) return;

    if (!formik.values.sequencialRoteiro && roteiroData && roteiroData.result.length) {
      const info = {
        codigo: roteiroData?.result[0]?.sequencialRoteiro,
        data: {
          documentoInclusao: roteiroData?.result[0]?.usuarioInclusao,
          nomeFormalInclusao: roteiroData?.result[0]?.nomeFormalInclusao,
          nomeFormalAlteracao: roteiroData?.result[0]?.nomeFormalAlteracao,
          documentoAlteracao: roteiroData?.result[0]?.usuarioAlteracao,
          dataInclusao: roteiroData?.result[0]?.dataInclusao,
          dataAlteracao: roteiroData?.result[0]?.dataAlteracao,
        },
      };

      setDadosInfo(info);

      formik.setValues({ ...roteiroData.result[0] });
    }

    if (roteiroErrors) {
      toast.error(roteiroErrors.message);
    }
  }, [formik, roteiroData, roteiroErrors, roteiroIsLoading]);

  const { associacaoListas } = useAssociacaoAtividades({
    roteiro: {
      sequencialRoteiro: formik.values.sequencialRoteiro,
      nome: formik.values.nome,
    },
  });

  return (
    <>
      {showErro ? (
        <>
          <br />
          <div className="alert alert-danger" role="alert">
            {messageErro}
          </div>
        </>
      ) : null}
      <h1 className="h3 mb-4 text-gray-800">Roteiro</h1>
      <div className="row">
        <div className="col-lg-12">
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <Row>
                <Col sm="11">
                  <h6 className="m-0 font-weight-bold text-primary flex-grow-1">
                    {formik.values.sequencialRoteiro ? "Alteração de Roteiro" : "Inclusão de Roteiro"}
                  </h6>
                </Col>
                <Col sm="1" className="text-right">
                  <OverlayInfoAdicional data={dadosInfo} keyFieldName="sequencialRoteiro" />
                </Col>
              </Row>
            </div>
            <div className="card-body">
              <FormAlert formik={formik} data={dataForm} errors={errorForm} isLoading={isLoading || roteiroIsLoading} />
              <Form noValidate onSubmit={formik.handleSubmit}>
                <fieldset
                  disabled={
                    formik.values.sequencialRoteiro
                      ? !isAllowed(funcionalidades.roteiro.alterar)
                      : !isAllowed(funcionalidades.roteiro.inserir)
                  }
                >
                  <Row>
                    <Form.Group as={Col} sm={12} md={3}>
                      <Form.Label>Sigla</Form.Label>
                      <TextInputFormik formik={formik} field="sigla" />
                    </Form.Group>
                    <Form.Group as={Col} sm={12} md={9}>
                      <Form.Label>Nome</Form.Label>
                      <TextInputFormik formik={formik} field="nome" maxLength="100" />
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} sm={12} md={12}>
                      <Form.Label>Descrição</Form.Label>
                      <TextInputFormik formik={formik} field="descricao" maxLength="240" />
                    </Form.Group>
                  </Row>
                  {formik.values.sequencialRoteiro ? (
                    <Row>
                      <Form.Group as={Col} sm={12} md={3}>
                        <CheckBoxFormik formik={formik} field="ativo" label="Ativo" />
                      </Form.Group>
                    </Row>
                  ) : null}
                  {viewBag.exibirAssociacao && associacaoListas}

                  {viewBag.exibirOrdenacao && (
                    <FormRoteiroOrdenacao
                      sequencialRoteiro={formik.values.sequencialRoteiro}
                      setMessageErro={setMessageErro}
                      setShowErro={setShowErro}
                    />
                  )}

                  {viewBag.exibirResumo && (
                    <FormRoteiroResumo
                      sequencialRoteiro={formik.values.sequencialRoteiro}
                      setMessageErro={setMessageErro}
                      setShowErro={setShowErro}
                    />
                  )}
                </fieldset>
                <Row className="mt-4">
                  <Col>
                    {!formik.isSubmitting && formik.values.sequencialRoteiro && (
                      <Button variant="primary" className="mr-3" onClick={handleAssociacao}>
                        Associar Atividades
                      </Button>
                    )}
                    {"    "}
                    {!formik.isSubmitting && formik.values.sequencialRoteiro && (
                      <Button variant="primary" className="mr-3" onClick={handleResumo}>
                        Resumo
                      </Button>
                    )}
                    {"    "}
                    {!formik.isSubmitting && formik.values.sequencialRoteiro && (
                      <Button variant="primary" className="mr-3" onClick={handleOrdenacao}>
                        Ordenar Atividades
                      </Button>
                    )}
                    {"    "}
                    {formik.isSubmitting ? (
                      <TextSpinner text="Registrando Roteiro..." />
                    ) : (
                      <ButtonSubmitFormikPermission
                        permission={
                          formik.values.sequencialRoteiro
                            ? funcionalidades.roteiro.alterar
                            : funcionalidades.roteiro.inserir
                        }
                        disabled={formik.isSubmitting}
                        name="Salvar"
                      />
                    )}
                    {"    "}
                    {!formik.isSubmitting && !formik.values.sequencialRoteiro && (
                      <Button variant="light" className="mr-3" onClick={formik.handleReset}>
                        Limpar
                      </Button>
                    )}
                    {"    "}
                    {!formik.isSubmitting && (
                      <Button variant="danger" onClick={() => history.goBack()}>
                        Cancelar
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FormRoteiro;
